import { FC, useCallback } from 'react';

import { useSelector } from 'src/hooks/useSelector';
import { Tag as CandidateTag, TagName } from 'src/models/candidatesList';

import AutoResponse from 'src/components/ResumeCard/Header/Tags/AutoResponse';
import AwaitingReply from 'src/components/ResumeCard/Header/Tags/AwaitingReply';
import FromChat from 'src/components/ResumeCard/Header/Tags/FromChat';
import FromPhoneCall from 'src/components/ResumeCard/Header/Tags/FromPhoneCall';
import MoreResponsesTag from 'src/components/ResumeCard/Header/Tags/MoreResponsesTag';
import SimpleTag, { SimpleTagType } from 'src/components/ResumeCard/Header/Tags/SimpleTag';
import VacancyQuestion from 'src/components/ResumeCard/Header/Tags/VacancyQuestion';

const SimpleTagMapper = {
    [TagName.AutoInvite]: SimpleTagType.AutoInvite,
    [TagName.AutoSurveyInProgress]: SimpleTagType.AutoSurveyInProgress,
    [TagName.ResponseFromVr]: SimpleTagType.FromVr,
    [TagName.ResponseFromClickmeCpa]: SimpleTagType.FromClickmeCpa,
};

interface Props {
    candidateTags: CandidateTag[];
    resumeHash?: string;
    vacancyId?: number;
}

const CandidateTags: FC<Props> = ({ candidateTags, resumeHash, vacancyId }) => {
    const funnelId = useSelector((state) => state.router.location.query.funnelId);

    const getCustomTagComponent = useCallback(
        (tag: CandidateTag) => {
            switch (tag.type) {
                case TagName.AwaitingReply:
                    return <AwaitingReply key={tag.type} data={tag.data} />;
                case TagName.IncomingCall:
                case TagName.MissedCall:
                    return <FromPhoneCall key={tag.type} type={tag.type} data={tag.data} />;
                case TagName.ResponseFromChat:
                    return <FromChat key={tag.type} type={tag.type} />;
                case TagName.ApplicantQuestion:
                    return <VacancyQuestion key={tag.type} />;
                case TagName.AutoResponse:
                    return <AutoResponse key={tag.type} />;
                case TagName.MoreResponses:
                    if (funnelId && resumeHash && vacancyId) {
                        return <MoreResponsesTag key={tag.type} resumeHash={resumeHash} vacancyId={vacancyId} />;
                    }
                    return null;
                default:
                    return null;
            }
        },
        [resumeHash, vacancyId, funnelId]
    );

    return (
        <>
            {candidateTags?.map((tag) => {
                if (tag.type in SimpleTagMapper) {
                    const simpleTagType = SimpleTagMapper[tag.type as keyof typeof SimpleTagMapper];
                    return <SimpleTag key={tag.type} type={simpleTagType} />;
                }
                return getCustomTagComponent(tag);
            })}
        </>
    );
};

export default CandidateTags;
